import { Grid, GridItem, Text } from '@chakra-ui/react';
import type { AiModelContent, AiModelId } from '@/constants/aiLeague/aiLeagueTypes';
import type { AiLeagueChartUserInput, AiLeagueLeaderboardEntry } from '@/hooks/useAiLeaguePageData';
import type { gameResult } from '@/models/gameUpdate';
import type { leagueMember } from '@/models/user';
import { pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from '@/views/ProfilePage/Card';
import { StandingsChartPanelSplit } from '@/components/standingsChart/StandingsChartPanel';
import { AiLeagueIntro } from './AiLeagueIntro';
import { AiLeagueLeaderboard } from './AiLeagueLeaderboard';
import { useAiLeagueStandingsData } from './useAiLeagueStandingsData';

/** Profile-style card shell shared by all four AI League desktop panels. */
const desktopCardProps = {
  borderRadius: 0,
  w: 'full' as const,
  h: 'full' as const,
  display: 'flex' as const,
  flexDirection: 'column' as const,
  minH: 0,
};

export function AiLeagueDesktopDashboard(props: {
  entries: AiLeagueLeaderboardEntry[];
  getMemberForRow: (entry: AiLeagueLeaderboardEntry) => leagueMember;
  tournamentStarted: boolean;
  settledMatchday: number;
  chartUser?: AiLeagueChartUserInput;
  aiModels: AiModelContent[];
  gameResults: gameResult[];
  isLoggedIn: boolean;
  hasUserList: boolean;
  onModelClick: (modelId: AiModelId) => void;
  onMethodologyClick: () => void;
}) {
  const standingsData = useAiLeagueStandingsData({
    gameResults: props.gameResults,
    settledMatchday: props.settledMatchday,
    aiModels: props.aiModels,
    chartUser: props.chartUser,
  });

  if (!standingsData.ready) {
    return null;
  }

  const chartPanelData = {
    series: standingsData.standings.series,
    maxMatchday: standingsData.standings.maxMatchday,
    columns: standingsData.columns,
    currentUid: standingsData.currentUid,
    gameResults: standingsData.gameResults,
  };

  return (
    <StandingsChartPanelSplit embedded {...chartPanelData}>
      {({ chartSection, matchdaySection }) => (
        <Grid
          templateColumns='1fr 1fr'
          templateRows='auto auto'
          gap={4}
          w='full'
          alignItems='stretch'
          id='standings-chart'
        >
          <GridItem minW={0} display='flex' flexDirection='column'>
            <Card {...desktopCardProps}>
              <Text {...pageSectionTitleProps} mb={2} flexShrink={0}>
                Leaderboard
              </Text>
              <AiLeagueLeaderboard
                entries={props.entries}
                getMemberForRow={props.getMemberForRow}
                tournamentStarted={props.tournamentStarted}
                showCompareCta={!props.hasUserList}
                isLoggedIn={props.isLoggedIn}
                onModelClick={props.onModelClick}
              />
            </Card>
          </GridItem>

          <GridItem minW={0} display='flex' flexDirection='column'>
            <Card {...desktopCardProps}>
              <Text {...pageSectionTitleProps} mb={2} flexShrink={0}>
                Points over time
              </Text>
              {chartSection}
            </Card>
          </GridItem>

          <GridItem minW={0} display='flex' flexDirection='column'>
            <Card {...desktopCardProps}>
              <AiLeagueIntro onMethodologyClick={props.onMethodologyClick} />
            </Card>
          </GridItem>

          <GridItem minW={0} display='flex' flexDirection='column'>
            <Card {...desktopCardProps}>{matchdaySection}</Card>
          </GridItem>
        </Grid>
      )}
    </StandingsChartPanelSplit>
  );
}
